import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// DE BALK — one segment per active truckwash, in a FIXED order that never
// re-sorts, because position is the identity: the eye learns "third from the
// left is Asten" instead of reading a label.
//
// The order comes from the LOCATION REGISTER (`balk_order` on the document),
// never from a list in this file and never from the name: a hardcoded list
// silently drops truckwash number twenty, and sorting by name would move a
// segment the day someone fixes a typo. `balk_order` is seeded from the original
// order so nothing moved when this changed hands.
const CLOSED = new Set(["opgelost", "afgewezen"]);
const DOWN = new Set(["defect", "in_reparatie"]);

export const balkSites = (locations) =>
  (locations || [])
    .filter((l) => l.type === "truckwash" && l.status === "active")
    .map((l) => ({ id: l.location_id, name: l.name, order: l.balk_order ?? 9999 }))
    .sort((a, b) => (a.order - b.order) || a.id.localeCompare(b.id));

// Exactly two conditions light a segment.
//
// ALARM (red, grows down): the site has an open P1 — it is stopped.
// STORING (orange, grows up): a machine ON A LANE has status `defect` or
// `in_reparatie`, i.e. the machine is genuinely down while the site keeps
// washing. Deliberately NOT "somebody reported something on that lane": that
// lit five of eighteen segments on real data, which is precisely the density the
// flag rule exists to prevent, and nobody learns to read a strip that shouts.
//
// Not P2. Not "aanvullen". Not an expiring inspection. Not low stock. Data
// completeness is administrative and may never light the balk.
export function computeBalk(faults, assets, locations) {
  const open = (faults || []).filter((f) => !CLOSED.has(f.status));
  // Round 22: a spoor is a property of the machine inside a was-hal, not a level
  // of its own. Same meaning as before -- a machine ON A LANE that is out of
  // service -- read from the field that now carries it. A machine in the
  // technische ruimte or on the buitenterrein still never lights a segment, and
  // neither does a ruimte's own state.
  const down = (assets || []).filter((a) => DOWN.has(a.status) && a.spoor);
  const sites = balkSites(locations);

  const segments = sites.map(({ id, name }) => {
    const p1 = open.find((f) => f.location_id === id && f.priority === "P1");
    if (p1) return { id, name, state: "alarm", fault: p1 };
    const broken = down.find((a) => a.location_id === id);
    if (broken) {
      // Link to the open fault on that machine when there is one; the segment is
      // still lit by the machine's own state, not by the report.
      const fault = open.find((f) => f.asset_id === broken.id) || null;
      return { id, name, state: "storing", fault };
    }
    return { id, name, state: "rust", fault: null };
  });

  return segments;
}

// THE STRIP IS FIXED LENGTH. It used to grow an extra "Niet in de balk" segment
// whenever work sat on a location outside it — written when the only way to be
// outside the strip was to BE an error. That is no longer true: the strip is
// truckwash-only by design, so every fault the TD logs at a pand or an opslag
// (`rucphen-32`, one open P3, no washing lanes at all) lit the band orange and
// made the aria-label announce a spoor out of service while nothing was down.
// Normal work may never light the balk, and the segment count must keep reading
// as "the sites in service".
//
// What "mismatch" always meant survives here instead: a TRUCKWASH that belongs
// in the strip but cannot be placed in it. That is a register error, so it is
// surfaced in the location register where it can be named and repaired — not as
// a 6px sliver that says nothing actionable.
const KNOWN_STATUS = new Set(["active", "planned", "closed"]);

export function balkMismatches(locations) {
  const truckwash = (locations || []).filter((l) => l.type === "truckwash");
  const found = [];

  const byOrder = new Map();
  truckwash
    .filter((l) => l.status === "active")
    .forEach((l) => {
      if (l.balk_order === null || l.balk_order === undefined) {
        found.push({ id: l.location_id, name: l.name, reason: "heeft geen plek in de balk" });
        return;
      }
      if (!byOrder.has(l.balk_order)) byOrder.set(l.balk_order, []);
      byOrder.get(l.balk_order).push(l);
    });

  // Position is identity in the strip, so two sites on one position means the
  // eye is being taught a position that can swap from one load to the next.
  byOrder.forEach((group) => {
    if (group.length < 2) return;
    group.forEach((l) => found.push({
      id: l.location_id,
      name: l.name,
      reason: `staat op dezelfde plek in de balk als ${group.filter((g) => g !== l).map((g) => g.name).join(", ")}`,
    }));
  });

  truckwash
    .filter((l) => !KNOWN_STATUS.has(l.status))
    .forEach((l) => found.push({
      id: l.location_id,
      name: l.name,
      reason: `heeft een onbekende status en valt daardoor buiten de balk`,
    }));

  return found.sort((a, b) => a.name.localeCompare(b.name));
}

export function balkLabel(segments, status = "ok") {
  if (status === "unknown") return "Toestand van de locaties onbekend: de gegevens konden niet worden opgehaald.";
  const alarm = segments.filter((s) => s.state === "alarm").map((s) => s.name);
  const storing = segments.filter((s) => s.state === "storing").map((s) => s.name);
  const parts = [`${segments.length} locaties`];
  if (alarm.length) parts.push(`${alarm.length} met P1: ${alarm.join(", ")}`);
  if (storing.length) parts.push(`${storing.length} met een spoor uit bedrijf: ${storing.join(", ")}`);
  if (!alarm.length && !storing.length) parts.push("alles in bedrijf");
  return `${parts.join(", ")}.`;
}

const BalkContext = createContext(null);

// One call, shared by every console on the page.
//
// A FAILURE IS ITS OWN STATE. It used to fall back to a resting strip, so one
// dropped request on wash-bay 4G rendered exactly like nineteen healthy sites —
// the one element whose job is chain status asserting "nothing is wrong" while a
// site stood still. `status` is explicit, so the distinction cannot be lost in a
// later refactor.
export const BalkProvider = ({ children }) => {
  const { canView } = useAuth();
  const [segments, setSegments] = useState([]);
  const [status, setStatus] = useState("unknown");

  const refresh = useCallback(async () => {
    try {
      // Assets carry the machine state that lights orange and are never
      // module-guarded; faults carry the P1 and are, so they are asked for only
      // when this user may see them.
      const [locations, assets, faults] = await Promise.all([
        api.get("/locations"),
        api.get("/assets"),
        canView("storingen") ? api.get("/faults?open_only=true") : Promise.resolve({ data: [] }),
      ]);
      setSegments(computeBalk(faults.data, assets.data, locations.data));
      setStatus("ok");
    } catch {
      setSegments([]);
      setStatus("unknown");
    }
  }, [canView]);

  // Every 60 seconds, and never faster: this is a 6px band, not a trading
  // screen, and the man reading it is on 4G in a wash bay. Writes that can
  // change it call `refresh()` directly.
  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 60000);
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVisible); };
  }, [refresh]);

  const value = useMemo(() => ({ segments, status, refresh }), [segments, status, refresh]);
  return <BalkContext.Provider value={value}>{children}</BalkContext.Provider>;
};

// null means "there is no strip here" (login, the public QR page). Consumers
// must not fabricate a resting strip out of it.
export const useBalkContext = () => useContext(BalkContext);
export const useBalk = () => useContext(BalkContext) || { segments: [], status: "unknown", refresh: () => {} };
